<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <q-card class="preview-file-card no-padding">
      <q-form @submit="save">
        <q-card-section class="row">
          <div class="full-width row q-pb-sm">
            <h6 class="q-ma-none">
              {{ dialogTitle || $t('signature.textTitle') }}
            </h6>
            <q-space />
            <q-btn
              flat
              color="dark-variant"
              icon="undo"
              @click="undo"
            >
              <q-tooltip>{{ $t('common.buttonUndo') }}</q-tooltip>
            </q-btn>
            <q-btn
              flat
              color="negative"
              icon="delete"
              @click="clear"
            >
              <q-tooltip>{{ $t('common.buttonClear') }}</q-tooltip>
            </q-btn>
          </div>
          <change-request-banner
            v-if="showChangeRequestBanner"
            is-in-signature-modal
            :original-session-activity="sessionActivity"
            :requested-changes="requestedChanges"
            :reason="reason"
          />
          <vue-signature-pad
            id="signature"
            ref="signaturePad"
            height="300px"
            :options="{
              resizeHandler
            }"
            data-e2e="signature-field"
          />
          <q-select
            v-if="showSignatoryOptions"
            v-model="signatoryId"
            :options="signatoryOptions"
            :disable="!anonymous"
            :label="$t('sessions.modals.signature.labelClientDataField')"
            emit-value
            map-options
            clearable
            hide-bottom-space
            outlined
            class="col-12 q-mt-sm"
            @update:model-value="onSelectSignatory"
          />
          <div class="col-12 q-mt-sm">
            <universal-tooltip
              :show-tooltip="mustEnterLegalName"
              :text="$t('sessions.modals.signature.inputLegalName')"
              self="center right"
            >
              <q-input
                v-model="signatoryName"
                :disable="!anonymous && !mustEnterLegalName"
                :label="$t('sessions.modals.signature.labelName')"
                data-e2e="input-signatory-name"
                :rules="[
                  val => (!anonymous && !mustEnterLegalName || (val.length > 3))
                    || $t('sessions.modals.signature.inputNameValidator')
                ]"
                hide-bottom-space
                lazy-rules
                outlined
                autocomplete="name"
              />
            </universal-tooltip>
          </div>

          <q-input
            v-if="includeTitle"
            v-model="title"
            :disable="!anonymous"
            :label="$t('sessions.modals.signature.labelTitle')"
            data-e2e="input-signatory-title"
            :rules="[
              val => (!anonymous || (val.length > 1))
                || $t('sessions.modals.signature.inputTitleValidator')
            ]"
            hide-bottom-space
            lazy-rules
            outlined
            class="col-12 q-mt-sm"
          />
        </q-card-section>
        <q-card-actions
          class="row q-px-md"
        >
          <div
            v-if="includeGeolocation && (isRequestingGeolocation || hasGeolocation)"
            class="col row items-center no-wrap text-dark-variant"
          >
            <q-icon
              name="place"
              color="positive"
              size="xs"
              class="q-mr-xs"
            />
            <q-spinner-puff
              v-if="isRequestingGeolocation"
              class="q-mr-sm"
            />
            <span
              v-if="hasGeolocation"
              class="ellipsis"
            >{{ geolocation.place }}</span>
            <span
              v-else
              class="ellipsis"
            >{{ $t('signature.textRequestingGeolocation') }}</span>
          </div>
          <div
            v-else
            class="col"
          />
          <div class="col-auto">
            <q-btn
              flat
              color="secondary"
              data-e2e="button-cancel-signature"
              @click="hide"
            >
              {{ $t('common.buttonCancel') }}
            </q-btn>
            <q-btn
              type="submit"
              flat
              color="primary"
              data-e2e="button-dialog-submit-signature"
            >
              {{ $t('signature.btnSignNow') }}
            </q-btn>
          </div>
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import ChangeRequestBanner from 'components/ChangeRequest/ChangeRequestBanner';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { chain, find, get, isEmpty } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { notifyNegative } from 'src/composables/interactions';
import { auth } from 'src/store/auth';
import { geolocation as geolocationStore } from 'src/store/geolocation';
import { network } from 'src/store/network';
import { VueSignaturePad } from 'vue-signature-pad';

export default {
  name: 'Signature',

  components: { VueSignaturePad, ChangeRequestBanner, UniversalTooltip },

  props: {
    anonymous: {
      type: Boolean,
      default: false,
    },

    userName: {
      type: String,
      default: '',
    },

    signatoryOptions: {
      type: Array,
      default: () => ([]),
    },

    initialSignatory: {
      type: String,
      default: null,
    },

    includeTitle: {
      type: Boolean,
      default: false,
    },

    includeGeolocation: {
      type: Boolean,
      default: false,
    },

    dialogTitle: {
      type: String,
      default: null,
    },

    sessionActivity: {
      type: Object,
      default: () => ({}),
    },

    requestedChanges: {
      type: Object,
      default: () => ({}),
    },

    reason: {
      type: String,
      default: '',
    },

    changeRequestStatus: {
      type: String,
      default: '',
    },

    userId: {
      type: String,
      default: '',
    },

  },

  emits: ['hide', 'ok'],

  data() {
    return {
      signatoryId: null,
      name: '',
      title: '',
      geolocation: null,
      prevWidth: null,
    };
  },

  computed: {
    ...mapState(geolocationStore, ['isRequestingGeolocation']),
    ...mapState(auth, ['userDetails', 'useLegalNameInSignature']),
    ...mapState(network, ['offlineMode']),

    hasGeolocation() {
      return !!get(this.geolocation, 'place');
    },

    showSignatoryOptions() {
      return !isEmpty(this.signatoryOptions);
    },

    signatoryName: {
      get() {
        if (this.anonymous || this.mustEnterLegalName) {
          return this.name;
        }

        return this.userName;
      },
      set(name) {
        if (this.anonymous || this.mustEnterLegalName) {
          this.name = name;
        }
      },
    },

    showChangeRequestBanner() {
      return this.sessionActivity?.status === 'pending-review' || this.changeRequestStatus === 'draft';
    },

    mustEnterLegalName() {
      return this.offlineMode && this.useLegalNameInSignature && (
        this.userId
        && (this.userId !== this.userDetails.userId)
      );
    },
  },

  watch: {
    initialSignatory: {
      immediate: true,
      handler(value) {
        if (find(this.signatoryOptions, { value: this.initialSignatory })) {
          this.signatoryId = value;
        }
      },
    },
  },

  methods: {
    ...mapActions(geolocationStore, ['executeGeolocationCallback']),

    show() {
      this.$refs.dialog.show();
      if (this.includeGeolocation) {
        this.getGeolocation();
      }
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    // changes data points so that landscape signature fits into a portrait view
    adjustLandscapeSignature(data, currentWidth, currentHeight, prevWidth) {
      let minX = null;
      let maxX = null;
      let minY = null;
      let maxY = null;

      if (!data?.length) {
        return data;
      }

      data.forEach((item) => {
        item.points.forEach((point) => {
          if (minX === null || minX > point.x) {
            minX = point.x;
          }
          if (maxX === null || maxX < point.x) {
            maxX = point.x;
          }
          if (minY === null || minY > point.y) {
            minY = point.y;
          }
          if (maxY === null || maxY < point.y) {
            maxY = point.y;
          }
        });
      });

      const xPointsDiff = maxX - minX;
      const yPointsDiff = maxY - minY;
      const signatureWiderThanPad = xPointsDiff > currentWidth;
      // scaling only if it doesn't already fit
      let scale = 1;

      if (signatureWiderThanPad) {
        // correction to make the signature take up all available width in the portrait orientation
        const signatureToFullSizeCorrection = ((xPointsDiff + 20) / prevWidth);
        scale = (currentWidth / prevWidth) / signatureToFullSizeCorrection;
      }

      const afterScaleWidthDiff = currentWidth - (xPointsDiff * scale);
      const afterScaleHeightDiff = currentHeight - (yPointsDiff * scale);
      // shifting is for centering the signature
      const shiftX = (minX * scale) - (afterScaleWidthDiff / 2);
      const shiftY = (minY * scale) - (afterScaleHeightDiff / 2);

      return data.map((item) => {
        return {
          ...item,
          points: item.points.map((point) => {
            return {
              ...point,
              x: (point.x * scale) - shiftX,
              y: (point.y * scale) - shiftY,
            };
          }),
        };
      });
    },

    resizeHandler() {
      const {
        $refs: { signaturePadCanvas },
        signaturePad,
        $el,
      } = this.$refs.signaturePad;
      $el.scrollIntoView();
      let data = signaturePad.toData();
      if ($el.offsetWidth < this.prevWidth) {
        data = this.adjustLandscapeSignature(data, $el.offsetWidth, $el.offsetHeight, this.prevWidth);
      }
      signaturePadCanvas.width = signaturePadCanvas.offsetWidth;
      signaturePadCanvas.height = signaturePadCanvas.offsetHeight;
      signaturePad.clear();
      signaturePad.fromData(data);
      this.prevWidth = $el.offsetWidth;
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    }, // onDialogHide()

    onSelectSignatory(signatoryId) {
      this.signatoryName = chain(this.signatoryOptions)
        .find({ value: signatoryId })
        .get('userName', this.signatoryName)
        .value();
    },

    getGeolocation() {
      this.executeGeolocationCallback((geolocation) => {
        if (!this || !geolocation) {
          // The dialog was destroyed while waiting for the geolocation
          return;
        }
        this.geolocation = geolocation;
      });
    },

    undo() {
      this.$refs.signaturePad.undoSignature();
    },

    clear() {
      this.$refs.signaturePad.clearSignature();
    },

    save() {
      const signature = this.$refs.signaturePad.saveSignature();

      if (signature.isEmpty) {
        notifyNegative(this.$t('signature.errorSignatureMissing'));
        return;
      }

      const emitData = {
        data: signature.data,
        userName: this.signatoryName,
      };

      if (this.showSignatoryOptions) {
        emitData.signatory = find(this.signatoryOptions, { value: this.signatoryId }) || null;
      }
      if (this.title) {
        emitData.title = this.title;
      }
      if (this.includeGeolocation) {
        emitData.geolocation = this.geolocation || null;
      }
      this.$emit('ok', emitData);
      this.hide();
    },
  },
};
</script>

<style scoped>
#signature {
    border: 2px solid #000;
    border-radius: 5px;
}
</style>
